import { useCallback, useEffect, useMemo, useState, useRef } from "react"
import axios from "axios"
import { useNavigate } from "react-router-dom"
import {
  Box, Button, Flex, HStack, Input, Select, Text, Badge, Spinner, Table, Tbody, Td, Th, Thead, Tr, useToast, Tabs, TabList, Tab, TabPanels, TabPanel,
  CircularProgress, CircularProgressLabel, Menu, MenuButton, MenuList, MenuItemOption, MenuOptionGroup, MenuDivider, MenuItem, Portal,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, NumberInput, NumberInputField, SimpleGrid,
} from "@chakra-ui/react"
import { FaFileExcel, FaChevronDown, FaRoute, FaSyncAlt, FaHome, FaMapMarkerAlt } from "react-icons/fa"

import PageToolbar from "../../components/shared/PageToolbar"
import VisitChecklistModal from "./VisitChecklistModal"
import { fmtR, fmtPct, pctTone } from "../rep/format"
import { Panel } from "../../components/shared/Section"
import ExportMenu from "../../components/shared/ExportMenu"
import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext"
import { neutral, signal, type } from "../../theme/tokens"

/**
 * Rep tracker (Field section, 2026-10-06 — backend docs/michael/rep-field/TASK-rep-tracker.md).
 * Blue Mango's Georep replacement, admin side (Linda / masters): the Georep strike-rate card for one, several or
 * all reps over a date range (optionally one store), per-rep table with ROAD km, every visit, km per day per rep,
 * the reps' homes (an address replaces the stand-in) and a re-plan. Georep history (31 Aug – 18 Sep) is included.
 * 2026-10-07: renamed "Rep Routing"; a **Store addresses** tab (a store's map point from an address); org admins
 * (Linda) VIEW everything — the edit buttons show only when the server says `canEdit` (masters for now).
 * 2026-10-12 (Helena): also rendered `embedded` under Rep Sales on the combined /rep page — there a login the
 * server does not treat as a routing admin gets nothing (no redirect, no error panel) instead of this page. Embedded,
 * the Per rep tab is dropped: its columns join the Rep Sales table per rep (onSummary hands the summary up).
 */
const API = process.env.REACT_APP_API_URL
const uid = () => localStorage.getItem("userid")
const pct = (a, b) => (b ? Math.round((100 * a) / b) : 0)
const kmFmt = (v) => (v == null ? "–" : Math.round(Number(v)).toLocaleString("en-ZA"))
const STATUS_LABEL = { planned: "To visit", visited: "Visited", late: "Caught up / early", missed: "Missed", additional: "Additional" }
const monthStart = (d) => `${d.slice(0, 7)}-01`

// jump = {tab: "stores" | "homes", n} — Rep Sales & Routing's top buttons open that tab and scroll to it (2026-10-16).
// compact (2026-10-16, the simpler Rep Sales & Routing page). The strike-rate panel was hidden here, then put back on Helena's word the same day.
// sales (2026-10-16, Helena "have it all in one"): the hub's every-store sales (rep_report/tracker, rep = all) for the
// supplier picked at the top — each visit line then shows that store's months and YoY. null = the visits table as before.
const RepTracker = ({ embedded = false, header = null, onSummary, jump = null, compact = false, sales = null }) => {
  const [tabIndex, setTabIndex] = useState(0)
  const tabsRef = useRef(null)
  useEffect(() => {
    if (!jump) return
    const base = embedded ? 0 : 1 // Per rep tab only when not embedded
    setTabIndex(base)
    setTimeout(() => { try { tabsRef.current && tabsRef.current.scrollIntoView({ behavior: "smooth", block: "start" }) } catch (e) { /* old browser */ } }, 50)
  }, [jump]) // eslint-disable-line react-hooks/exhaustive-deps
  const toast = useToast()
  const navigate = useNavigate()
  const { selectedOrg, isMaster } = useSupplierRetailer()
  const orgArg = useMemo(() => (isMaster ? { org_code: selectedOrg } : {}), [isMaster, selectedOrg])
  const post = useCallback((path, body = {}) => axios.post(`${API}/field/${path}`, { userid: uid(), ...orgArg, ...body }), [orgArg])
  const salesBy = useMemo(() => {
    if (!sales || !Array.isArray(sales.stores)) return null
    const m = new Map()
    for (const r of sales.stores) {
      const code = String(r.site_code || "")
      m.set(code.toUpperCase(), r)
      const sp = code.indexOf(" ")
      if (sp > 0 && !m.has(code.slice(sp + 1).toUpperCase())) m.set(code.slice(sp + 1).toUpperCase(), r)
    }
    return m
  }, [sales])
  const salesOf = (r) => salesBy && (salesBy.get(`${r.retailer || "DIS"} ${r.site_code}`.toUpperCase()) || salesBy.get(String(r.site_code || "").toUpperCase()))
  const L = sales?.period?.labels || {}
  // Visits tab: the visit whose checklist + photos are open (2026-10-16, Helena "where do I see the photos")
  const [photoVisit, setPhotoVisit] = useState(null)

  const [me, setMe] = useState(null)
  const [error, setError] = useState(null)
  const [reps, setReps] = useState([]) // [] = all
  const showRep = reps.length !== 1 // all reps (or several) → the Rep column; one rep picked → not
  // "Not assigned" (2026-10-16): the stores with sales and no rep (the hub's every-store sales, rep_name empty)
  const [noRep, setNoRep] = useState(false)
  // Not assigned = the same stores as Data → Rep stores' "No rep" (Select rep + No rep assigned; Not Blue Mango left out,
  // Helena 2026-10-16), each with its sales from the hub's every-store figures
  const [noRepList, setNoRepList] = useState(null)
  const noRepStores = useMemo(() => {
    if (!noRepList) return []
    const by = new Map()
    for (const r of (sales && Array.isArray(sales.stores) ? sales.stores : [])) {
      const code = String(r.site_code || "").toUpperCase()
      by.set(code, r)
      const m = /^([A-Z]+) (.+)$/.exec(code)
      if (m) { by.set(`${m[1] === "MED" ? "CHK" : m[1]} ${m[2]}`, r); if (!by.has(m[2])) by.set(m[2], r) }
    }
    return noRepList.filter((x) => x.status === "select" || x.status === "no_rep").map((x) => {
      const v = by.get(`${x.retailer} ${x.site_code}`.toUpperCase()) || by.get(String(x.site_code).toUpperCase()) || {}
      return { ...v, site_code: x.site_code, site_description: x.site_description, retailer: x.retailer, store_status: x.status }
    })
  }, [noRepList, sales])
  const [from, setFrom] = useState("")
  const [to, setTo] = useState("")
  const [site, setSite] = useState("")
  const [sum, setSum] = useState(null)
  const [rows, setRows] = useState(null)
  const [busy, setBusy] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [edit, setEdit] = useState(null) // home being edited
  const [storeList, setStoreList] = useState(null) // Store addresses tab
  const [storeEdit, setStoreEdit] = useState(null)
  const [storeQ, setStoreQ] = useState("")
  // Rep maintenance (2026-10-16, Helena: Linda keeps the rep lists up to date herself)
  const [storeFilter, setStoreFilter] = useState("missing") // missing | norep | all
  const [storeReps, setStoreReps] = useState([])
  const [repSaving, setRepSaving] = useState(null) // retailer|site being saved
  const [addStore, setAddStore] = useState(null)
  const [addRep, setAddRep] = useState(null)

  useEffect(() => {
    post("me").then((r) => {
      const m = r.data.data
      if (m.role !== "admin") {
        if (embedded) return setError("hidden")
        return navigate("/field/route", { replace: true })
      }
      setMe(m)
      // Always the current month to today (Helena 2026-10-13: "make the default the current month (always)") — was from 31 Aug.
      setFrom(monthStart(m.today))
      setTo(m.today)
      // Opens on the FIRST rep of the list, not all reps (Helena 2026-10-16); "All reps" stays in the menu
      if (Array.isArray(m.reps) && m.reps.length) setReps([m.reps[0]])
    }).catch((e) => setError(embedded ? "hidden" : e?.response?.data?.message || e.message))
  }, [post, navigate, embedded])

  const filters = useMemo(() => ({ reps, from, to, site_code: site || undefined }), [reps, from, to, site])
  useEffect(() => {
    if (!me || !sales) return
    post("stores").then((r) => setNoRepList(r.data.data || [])).catch(() => setNoRepList([]))
  }, [me, !!sales]) // eslint-disable-line react-hooks/exhaustive-deps
  const load = useCallback(() => {
    if (!me || !from || !to) return
    setSum(null); setRows(null)
    post("summary", filters).then((r) => setSum(r.data.data)).catch((e) => setError(e?.response?.data?.message || e.message))
    post("detail", filters).then((r) => setRows(r.data.data)).catch(() => setRows([]))
  }, [me, from, to, filters, post])
  useEffect(load, [load])
  // Embedded: the Rep Sales table's routing columns are this month (1st → month end, every rep; visits to date),
  // in step with its "MTD" sales column — independent of this section's own date filters (Helena, 2026-10-12).
  useEffect(() => {
    if (!me || !onSummary) return
    // Whole month: visits exist only up to today, and the month's planned km is the budget ("est km").
    const [y, m] = me.today.split("-").map(Number)
    const mtd = { reps: [], from: monthStart(me.today), to: `${me.today.slice(0, 7)}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}` }
    post("summary", mtd).then((r) => onSummary({ ...r.data.data, from: mtd.from, to: mtd.to })).catch(() => onSummary(null))
  }, [me, post, onSummary])

  const replan = async () => {
    setBusy(true)
    try {
      const r = await post("replan", { rep: "ALL" })
      toast({ title: "Routes re-planned", description: `${r.data.data.length} reps, from today to month end`, status: "success", isClosable: true })
      load()
    } catch (e) {
      toast({ title: "Re-plan failed", description: e?.response?.data?.message || e.message, status: "error", isClosable: true })
    } finally { setBusy(false) }
  }
  const saveHome = async () => {
    setBusy(true)
    try {
      const body = { rep: edit.rep_name, label: edit.label, stores_per_day: edit.stores_per_day }
      if (edit.lat !== "" && edit.lng !== "") { body.lat = Number(edit.lat); body.lng = Number(edit.lng) }
      const r = await post("home", body)
      toast({ title: "Home saved and route re-planned", description: `${r.data.data.plan.planned + r.data.data.plan.repeats} visits · ${r.data.data.plan.km} road km to month end`, status: "success", isClosable: true })
      setEdit(null); load()
    } catch (e) {
      toast({ title: "Not saved", description: e?.response?.data?.message || e.message, status: "error", isClosable: true })
    } finally { setBusy(false) }
  }
  const loadStores = useCallback(() => {
    if (!me) return
    setStoreList(null)
    post("stores").then((r) => { setStoreList(r.data.data || []); setStoreReps(r.data.reps || []) }).catch(() => setStoreList([]))
  }, [me, post])
  useEffect(loadStores, [loadStores])
  const saveStore = async () => {
    setBusy(true)
    try {
      const body = { site_code: storeEdit.site_code, retailer: storeEdit.retailer, address: storeEdit.address }
      if (storeEdit.lat !== "" && storeEdit.lng !== "") { body.lat = Number(storeEdit.lat); body.lng = Number(storeEdit.lng) }
      const r = await post("store-address", body)
      toast({ title: "Store location saved", description: `Re-planned from tomorrow: ${r.data.data.reps.join(", ")}`, status: "success", isClosable: true })
      setStoreEdit(null); loadStores(); load()
    } catch (e) {
      toast({ title: "Not saved", description: e?.response?.data?.message || e.message, status: "error", isClosable: true })
    } finally { setBusy(false) }
  }
  const changeRep = async (s, rep) => {
    const key = `${s.retailer}|${s.site_code}`
    setRepSaving(key)
    try {
      await post("store-rep", { site_code: s.site_code, retailer: s.retailer, rep })
      setStoreList((list) => list.map((x) => (`${x.retailer}|${x.site_code}` === key ? { ...x, rep: rep || null, reps: rep || null } : x)))
      toast({ title: rep ? `${s.site_description} → ${rep}` : `${s.site_description} → No rep`, description: "Routes re-planned from tomorrow", status: "success", duration: 2500, isClosable: true })
      load()
    } catch (e) {
      toast({ title: "Not saved", description: e?.response?.data?.message || e.message, status: "error", isClosable: true })
    } finally { setRepSaving(null) }
  }
  const saveAddStore = async () => {
    setBusy(true)
    try {
      const r = await post("store-add", addStore)
      const d = r.data.data
      toast({ title: `${d.site_description} added for ${d.rep}`, description: d.hasPoint ? "Route re-planned from tomorrow" : "No map location yet — set its address so it can be routed", status: d.hasPoint ? "success" : "warning", duration: 6000, isClosable: true })
      setAddStore(null); loadStores(); load()
    } catch (e) {
      toast({ title: "Not added", description: e?.response?.data?.message || e.message, status: "error", isClosable: true })
    } finally { setBusy(false) }
  }
  const saveAddRep = async () => {
    setBusy(true)
    try {
      const body = { rep_name: addRep.rep_name, home_label: addRep.home_label, stores_per_day: addRep.stores_per_day }
      if (addRep.lat !== "" && addRep.lng !== "") { body.lat = Number(addRep.lat); body.lng = Number(addRep.lng) }
      const r = await post("rep-add", body)
      toast({ title: `${r.data.data.rep_name} added`, description: "Give the rep stores on Data → Stores. The phone login is made on Users → Reps.", status: "success", duration: 6000, isClosable: true })
      setAddRep(null)
      const m = await post("me"); setMe(m.data.data)
      load(); loadStores()
    } catch (e) {
      toast({ title: "Not added", description: e?.response?.data?.message || e.message, status: "error", isClosable: true })
    } finally { setBusy(false) }
  }
  const exportXlsx = async () => {
    setExporting(true)
    try {
      const res = await fetch(`${API}/field/export`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ userid: uid(), ...orgArg, ...filters }) })
      if (!res.ok || !(res.headers.get("content-type") || "").includes("spreadsheetml")) throw new Error(`HTTP ${res.status}`)
      const blob = await res.blob()
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `Rep Routing ${from} to ${to}.xlsx`
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href)
    } catch (e) {
      toast({ title: "Download failed", description: e.message, status: "error", isClosable: true })
    } finally { setExporting(false) }
  }

  if (error === "hidden") return null
  if (embedded && !me) return null
  if (error) return <Panel><Text fontSize={type.size.small} color={signal.down}>{error}</Text></Panel>
  if (!me) return <Panel><Spinner size="sm" /></Panel>
  const t = sum?.total
  const strike = t ? pct(t.visited + t.late, t.scheduled) : 0
  const kmByRep = new Map((sum?.perRep || []).map((r) => [r.rep_name, r]))
  // the panel's summary block: routing from the summary's per-rep rows, sales + store count from the hub's every-store
  // sales (the reps picked; one store when a store is picked)
  const extra = (() => { // plain (no hook): this runs after the early returns above
    const pr = sum?.perRep || []
    const n = (k) => pr.reduce((a, r) => a + (Number(r[k]) || 0), 0)
    const out = { km_est: n("km_est"), km_done: n("km_done"), days_out: n("days_out"), stores: null, sales: null }
    if (sales && Array.isArray(sales.stores)) {
      const want = new Set(reps)
      // "Not assigned" (2026-10-16): only the stores with sales and no rep
      let rows = noRep ? noRepStores : sales.stores.filter((r) => r.rep_name && (want.size === 0 || want.has(r.rep_name)))
      if (site) rows = rows.filter((r) => String(r.site_code).toUpperCase().endsWith(String(site).toUpperCase()))
      out.stores = rows.length
      const tot = (k) => rows.reduce((a, r) => (r[k] == null ? a : a + Number(r[k])), 0)
      const yoy = (ly, ty) => (ly ? (ty - ly) / ly : null)
      const cl = tot("cur_ly"), ct = tot("cur_ty"), pl = tot("prev_ly"), pt = tot("prev_ty")
      out.sales = { cur_ly: cl, cur_ty: ct, cur_yoy: yoy(cl, ct), prev_ly: pl, prev_ty: pt, prev_yoy: yoy(pl, pt) }
    }
    return out
  })()
  const kmDays = sum?.kmDays || []

  return (
    <Box>
      {header}
      <PageToolbar
        description={embedded ? `Rep Routing${me.canEdit ? "" : " · view only"}` : `${me.orgName} · Rep Routing${me.canEdit ? "" : " · view only"}`}
        filters={
          <>
            <Menu closeOnSelect={false}>
              <MenuButton as={Button} size="sm" variant="outline" rightIcon={<FaChevronDown />} fontWeight={400}>
                {noRep ? "Not assigned" : reps.length === 0 ? "All reps" : reps.length === 1 ? reps[0] : `${reps.length} reps`}
              </MenuButton>
              <Portal>
                <MenuList maxH="360px" overflowY="auto" zIndex={1500}>
                  <MenuItem onClick={() => { setNoRep(false); setReps([]) }}>All reps</MenuItem>
                  <MenuDivider />
                  <MenuOptionGroup type="checkbox" value={reps} onChange={(v) => { setNoRep(false); setReps(Array.isArray(v) ? v : [v]) }}>
                    {me.reps.map((r) => <MenuItemOption key={r} value={r}>{r}</MenuItemOption>)}
                  </MenuOptionGroup>
                  {sales && <><MenuDivider />
                    <MenuItem fontWeight={noRep ? 700 : 400} onClick={() => { setReps([]); setNoRep(true); setTabIndex(0) }}>Not assigned{noRepStores.length ? ` (${noRepStores.length} stores)` : ""}</MenuItem></>}
                </MenuList>
              </Portal>
            </Menu>
            <Input size="sm" type="date" w="auto" value={from} onChange={(e) => setFrom(e.target.value)} />
            <Text fontSize={type.size.small} color={neutral[500]}>to</Text>
            <Input size="sm" type="date" w="auto" value={to} onChange={(e) => setTo(e.target.value)} />
            <Select size="sm" w="auto" maxW="300px" value={site} onChange={(e) => setSite(e.target.value)}>
              <option value="">All stores</option>
              {(sum?.sites || []).map((s) => <option key={s.site_code} value={s.site_code}>{s.site_description} ({s.site_code})</option>)}
            </Select>
          </>
        }
        actions={
          <HStack>
            {me.canEdit && <Button size="sm" variant="outline" leftIcon={<FaSyncAlt />} onClick={replan} isLoading={busy}>Re-plan routes</Button>}
            <ExportMenu isLoading={exporting} width="260px" items={[{ label: "Excel · strike rate, visits, km", onClick: exportXlsx, icon: FaFileExcel }]} />
          </HStack>
        }
      />

      {/* the STRIKE RATE panel shows on the simpler page too (Helena 2026-10-16: "this little section is missing now, put this back") */}
      {<Panel mb={4}>
        <Text fontWeight={700} fontSize={type.size.small} letterSpacing="0.04em" color={neutral[600]} mb={3}>STRIKE RATE</Text>
        {!t ? <Spinner size="sm" /> : (
          <Flex gap={6} align="center" wrap={{ base: "wrap", xl: "nowrap" }}>
            <CircularProgress value={noRep ? 0 : strike} size="120px" thickness="10px" color="blue.700" trackColor={neutral[100]}>
              <CircularProgressLabel fontSize="1.6rem" fontWeight={600} color="blue.800">{noRep ? "–" : `${strike}%`}</CircularProgressLabel>
            </CircularProgress>
            <Flex gap={6} wrap={{ base: "wrap", xl: "nowrap" }} flexShrink={0} pl={{ base: 0, lg: 6 }}>
              {/* columns of three (Helena 2026-10-16): # stores / scheduled / visited · missed / additional / caught up · still today / days out / km | this month LY / TY / YoY · last month */}
              {(() => {
                const Row = ({ k, children }) => (
                  <Flex justify="space-between" align="flex-end" borderBottom="1px solid" borderColor={neutral[200]} py={1} gap={4} minW="170px">
                    <Text fontSize={type.size.small} color={neutral[600]}>{k}</Text>{children}
                  </Flex>
                )
                const V = ({ v, tone }) => <Text fontSize={type.size.body} fontWeight={700} color={tone === undefined ? "blue.800" : pctTone(tone, signal, neutral)}>{v}</Text>
                const cols = [
                  <Box key="v1">
                    <Row k="# stores"><V v={extra.stores == null ? "–" : extra.stores.toLocaleString("en-ZA")} /></Row>
                    <Row k="Scheduled visits"><V v={noRep ? "–" : (t.scheduled).toLocaleString("en-ZA")} /></Row>
                    <Row k="Visited on schedule"><V v={noRep ? "–" : (t.visited + t.late).toLocaleString("en-ZA")} /></Row>
                  </Box>,
                  <Box key="v2">
                    <Row k="Missed visits"><V v={noRep ? "–" : t.missed.toLocaleString("en-ZA")} tone={!noRep && t.missed ? -1 : undefined} /></Row>
                    <Row k="Additional visits"><V v={noRep ? "–" : (t.additional).toLocaleString("en-ZA")} /></Row>
                    <Row k="Caught up / early"><V v={noRep ? "–" : (t.late).toLocaleString("en-ZA")} /></Row>
                  </Box>,
                  <Box key="v3" pr={{ base: 0, xl: 6 }} borderRight={{ base: "none", xl: "1px solid" }} borderColor={neutral[200]}>
                    <Row k="Still to visit today"><V v={noRep ? "–" : (t.open_today).toLocaleString("en-ZA")} /></Row>
                    <Row k="Days out"><V v={noRep ? "–" : extra.days_out.toLocaleString("en-ZA")} /></Row>
                    <Row k="Km">
                      <Box textAlign="right" lineHeight={1.1}>
                        <Text fontSize="11px" fontWeight={600} color={signal.down} title="Planned route km this period (the km budget)">est {noRep ? "–" : Math.round(extra.km_est).toLocaleString("en-ZA")}</Text>
                        <V v={noRep ? "–" : Math.round(extra.km_done).toLocaleString("en-ZA")} />
                      </Box>
                    </Row>
                  </Box>,
                ]
                if (extra.sales) {
                  cols.push(
                    <Box key="c">
                      <Row k={L.curLY || "LY"}><V v={fmtR(extra.sales.cur_ly)} /></Row>
                      <Row k={L.cur || "This month"}><V v={fmtR(extra.sales.cur_ty)} /></Row>
                      <Row k="YoY"><V v={fmtPct(extra.sales.cur_yoy)} tone={extra.sales.cur_yoy} /></Row>
                    </Box>,
                    <Box key="p">
                      <Row k={L.prevLY || "LY"}><V v={fmtR(extra.sales.prev_ly)} /></Row>
                      <Row k={L.prev || "Last month"}><V v={fmtR(extra.sales.prev_ty)} /></Row>
                      <Row k="YoY"><V v={fmtPct(extra.sales.prev_yoy)} tone={extra.sales.prev_yoy} /></Row>
                    </Box>,
                  )
                }
                return cols
              })()}
            </Flex>
          </Flex>
        )}
      </Panel>}

      <Box ref={tabsRef} />
      <Tabs variant="enclosed" colorScheme="teal" isLazy index={tabIndex} onChange={setTabIndex}>
        <TabList flexWrap="wrap">
          {!embedded && <Tab fontSize={type.size.small}>Per rep</Tab>}
          <Tab fontSize={type.size.small}>Visits</Tab>
          <Tab fontSize={type.size.small}>Km per day</Tab>
        </TabList>
        <TabPanels>
          {!embedded && <TabPanel px={0}>
            <Panel>
              {!sum ? <Spinner size="sm" /> : (
                <Box overflowX="auto">
                  <Table size="sm" sx={{ "td, th": { fontSize: type.size.caption, py: 1.5 } }}>
                    <Thead bg={neutral[50]}><Tr><Th>Rep</Th><Th isNumeric>Scheduled</Th><Th isNumeric>Visited on schedule</Th><Th isNumeric>Caught up / early</Th><Th isNumeric>Additional</Th><Th isNumeric>Missed</Th><Th isNumeric>Strike rate</Th><Th isNumeric>Road km</Th><Th isNumeric>Days</Th><Th></Th></Tr></Thead>
                    <Tbody>
                      {sum.perRep.map((r) => (
                        <Tr key={r.rep_name}>
                          <Td fontWeight={600}>{r.rep_name}</Td>
                          <Td isNumeric>{r.scheduled}</Td><Td isNumeric>{r.visited + r.late}</Td><Td isNumeric>{r.late}</Td><Td isNumeric>{r.additional}</Td>
                          <Td isNumeric color={r.missed ? signal.down : undefined}>{r.missed}</Td>
                          <Td isNumeric fontWeight={700}>{pct(r.visited + r.late, r.scheduled)}%</Td>
                          <Td isNumeric>{kmFmt(r.km)}</Td><Td isNumeric>{r.days}</Td>
                          <Td><Button size="xs" variant="ghost" leftIcon={<FaRoute />} onClick={() => navigate(`/field/route?rep=${encodeURIComponent(r.rep_name)}`)}>Route</Button></Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </Panel>
          </TabPanel>}

          <TabPanel px={0}>
            <Panel>
              {noRep && salesBy ? (
                <Box overflowX="auto" maxH="620px" overflowY="auto">
                  <Text fontSize={type.size.small} color={neutral[600]} mb={2}>{noRepStores.length} store{noRepStores.length === 1 ? "" : "s"} with no rep — the same list as Data → Rep stores "No rep" (Not Blue Mango left out). Give one a rep there.</Text>
                  <Table size="sm" sx={{ "td, th": { fontSize: type.size.caption, py: 1 } }}>
                    <Thead bg={neutral[50]} position="sticky" top={0} zIndex={1}><Tr><Th>Store code</Th><Th>Store name</Th><Th>Status</Th>
                      <Th isNumeric>{L.curLY || "LY"}</Th><Th isNumeric>{L.cur || "This month"}</Th><Th isNumeric>YoY</Th>
                      <Th isNumeric>{L.prevLY || "LY"}</Th><Th isNumeric>{L.prev || "Last month"}</Th><Th isNumeric>YoY</Th></Tr></Thead>
                    <Tbody>
                      {noRepStores.map((v) => (
                        <Tr key={v.site_code}>
                          <Td whiteSpace="nowrap">{String(v.site_code).replace(/^[A-Z]+ /, "")}</Td><Td>{v.site_description}</Td><Td whiteSpace="nowrap"><Badge colorScheme={v.store_status === "no_rep" ? "orange" : "yellow"}>{v.store_status === "no_rep" ? "No rep assigned" : "Select rep"}</Badge></Td>
                          <Td isNumeric whiteSpace="nowrap">{fmtR(v.cur_ly)}</Td><Td isNumeric whiteSpace="nowrap">{fmtR(v.cur_ty)}</Td>
                          <Td isNumeric whiteSpace="nowrap" color={pctTone(v.cur_yoy, signal, neutral)}>{fmtPct(v.cur_yoy)}</Td>
                          <Td isNumeric whiteSpace="nowrap">{fmtR(v.prev_ly)}</Td><Td isNumeric whiteSpace="nowrap">{fmtR(v.prev_ty)}</Td>
                          <Td isNumeric whiteSpace="nowrap" color={pctTone(v.prev_yoy, signal, neutral)}>{fmtPct(v.prev_yoy)}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              ) : !rows ? <Spinner size="sm" /> : (
                <Box overflowX="auto" maxH="620px" overflowY="auto">
                  <Table size="sm" sx={{ "td, th": { fontSize: type.size.caption, py: 1 } }}>
                    <Thead bg={neutral[50]} position="sticky" top={0} zIndex={1}>{salesBy ? (
                      <Tr><Th>Date</Th>{showRep && <Th>Rep</Th>}<Th>Store code</Th><Th>Store name</Th>
                        <Th isNumeric>{L.curLY || "LY"}</Th><Th isNumeric>{L.cur || "This month"}</Th><Th isNumeric>YoY</Th>
                        <Th isNumeric>{L.prevLY || "LY"}</Th><Th isNumeric>{L.prev || "Last month"}</Th><Th isNumeric>YoY</Th>
                        <Th>Visit</Th><Th>Result</Th><Th isNumeric>Leg km</Th><Th>Checklist</Th><Th>Comment</Th></Tr>
                    ) : (
                      <Tr><Th>Date</Th><Th>Rep</Th><Th>#</Th><Th>Store</Th><Th>Visit</Th><Th>Result</Th><Th isNumeric>Leg km</Th><Th>Checklist</Th><Th>Comment</Th><Th>Source</Th></Tr>
                    )}</Thead>
                    <Tbody>
                      {salesBy && rows.slice(0, 1500).map((r) => { const v = salesOf(r) || {}; return (
                        <Tr key={r.id}>
                          <Td whiteSpace="nowrap">{r.plan_date}</Td>{showRep && <Td whiteSpace="nowrap">{r.rep_name}</Td>}
                          <Td whiteSpace="nowrap">{r.site_code}</Td><Td>{r.site_description}</Td>
                          <Td isNumeric whiteSpace="nowrap">{fmtR(v.cur_ly)}</Td><Td isNumeric whiteSpace="nowrap">{fmtR(v.cur_ty)}</Td>
                          <Td isNumeric whiteSpace="nowrap" color={pctTone(v.cur_yoy, signal, neutral)}>{fmtPct(v.cur_yoy)}</Td>
                          <Td isNumeric whiteSpace="nowrap">{fmtR(v.prev_ly)}</Td><Td isNumeric whiteSpace="nowrap">{fmtR(v.prev_ty)}</Td>
                          <Td isNumeric whiteSpace="nowrap" color={pctTone(v.prev_yoy, signal, neutral)}>{fmtPct(v.prev_yoy)}</Td>
                          <Td>{r.visit_kind === "repeat" ? "2nd" : "1st"}</Td>
                          <Td><Badge colorScheme={{ visited: "green", late: "teal", missed: "red", additional: "purple" }[r.status] || "gray"}>{STATUS_LABEL[r.status] || r.status}</Badge></Td>
                          <Td isNumeric>{r.leg_km == null ? "" : Number(r.leg_km).toFixed(1)}</Td>
                          <Td whiteSpace="nowrap">{r.photos || r.checked
                            ? <Button size="xs" variant="outline" colorScheme="teal" onClick={() => setPhotoVisit(r)} title="Ticks and photos per supplier">{`📷 ${r.photos || 0} · ✓ ${r.checked || 0}`}</Button>
                            : ""}</Td>
                          <Td maxW="260px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={r.comment || ""}>{r.comment}</Td>
                        </Tr>
                      ) })}
                      {!salesBy && rows.slice(0, 1500).map((r) => (
                        <Tr key={r.id}>
                          <Td whiteSpace="nowrap">{r.plan_date}</Td><Td whiteSpace="nowrap">{r.rep_name}</Td><Td>{r.status === "additional" ? "+" : r.seq}</Td>
                          <Td>{r.site_description}{r.site_code ? ` (${r.site_code})` : ""}</Td>
                          <Td>{r.visit_kind === "repeat" ? "2nd" : "1st"}</Td>
                          <Td><Badge colorScheme={{ visited: "green", late: "teal", missed: "red", additional: "purple" }[r.status] || "gray"}>{STATUS_LABEL[r.status] || r.status}</Badge></Td>
                          <Td isNumeric>{r.leg_km == null ? "" : Number(r.leg_km).toFixed(1)}</Td>
                          <Td whiteSpace="nowrap">{r.photos || r.checked
                            ? <Button size="xs" variant="outline" colorScheme="teal" onClick={() => setPhotoVisit(r)} title="Ticks and photos per supplier">{`📷 ${r.photos || 0} · ✓ ${r.checked || 0}`}</Button>
                            : ""}</Td>
                          <Td maxW="260px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={r.comment || ""}>{r.comment}</Td>
                          <Td>{r.source === "georep" ? "Georep" : "Plan"}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                  {rows.length > 1500 && <Text fontSize={type.size.caption} color={neutral[500]} mt={2}>Showing 1,500 of {rows.length} — narrow the filters or download Excel.</Text>}
                  <VisitChecklistModal visit={photoVisit} body={{ userid: uid(), ...orgArg }} onClose={() => setPhotoVisit(null)} />
                </Box>
              )}
            </Panel>
          </TabPanel>

          <TabPanel px={0}>
            <Panel>
              {!sum ? <Spinner size="sm" /> : kmDays.length === 0 ? <Text fontSize={type.size.small} color={neutral[500]}>No planned days in this range (Georep history carries no road km).</Text> : (
                <Box overflowX="auto" maxH="620px" overflowY="auto">
                  <Table size="sm" sx={{ "td, th": { fontSize: type.size.caption, py: 1 } }}>
                    <Thead bg={neutral[50]} position="sticky" top={0}><Tr><Th>Rep</Th><Th>Date</Th><Th isNumeric>Stores</Th><Th isNumeric>Road km</Th></Tr></Thead>
                    <Tbody>
                      {[...new Set(kmDays.map((d) => d.rep_name))].sort().map((rep) => {
                        const ds = kmDays.filter((d) => d.rep_name === rep)
                        return [
                          ...ds.map((d) => (
                            <Tr key={`${rep}${d.plan_date}`}><Td>{rep}</Td><Td>{d.plan_date}</Td><Td isNumeric>{d.stores}</Td><Td isNumeric>{Number(d.km).toFixed(0)}{d.km_source === "estimate" ? "*" : ""}</Td></Tr>
                          )),
                          <Tr key={`${rep}-total`} bg={neutral[50]}><Td fontWeight={700}>{rep} — total</Td><Td></Td><Td isNumeric fontWeight={700}>{ds.reduce((a, d) => a + d.stores, 0)}</Td><Td isNumeric fontWeight={700}>{kmFmt(ds.reduce((a, d) => a + Number(d.km || 0), 0))}</Td></Tr>,
                        ]
                      })}
                    </Tbody>
                  </Table>
                  <Text fontSize={type.size.caption} color={neutral[500]} mt={2}>Road km from home through the day's stores and back home (OpenStreetMap roads). * = estimated (the road service was unavailable).</Text>
                </Box>
              )}
            </Panel>
          </TabPanel>

        </TabPanels>
      </Tabs>

      <Modal isOpen={!!storeEdit} onClose={() => setStoreEdit(null)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Store address — {storeEdit?.site_description}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize={type.size.small} color={neutral[600]} mb={2}>Street address (found on the map), or the coordinates. Saving re-plans the reps with this store from tomorrow.</Text>
            <Input mb={3} placeholder="e.g. Shop 12, Canal Walk, Century Boulevard, Cape Town" value={storeEdit?.address || ""} onChange={(e) => setStoreEdit({ ...storeEdit, address: e.target.value })} />
            <HStack>
              <Input placeholder="Latitude (optional)" value={storeEdit?.lat || ""} onChange={(e) => setStoreEdit({ ...storeEdit, lat: e.target.value })} />
              <Input placeholder="Longitude (optional)" value={storeEdit?.lng || ""} onChange={(e) => setStoreEdit({ ...storeEdit, lng: e.target.value })} />
            </HStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={() => setStoreEdit(null)}>Cancel</Button>
            <Button colorScheme="teal" onClick={saveStore} isLoading={busy} isDisabled={!storeEdit?.address && (storeEdit?.lat === "" || storeEdit?.lng === "")}>Save &amp; re-plan</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={!!addStore} onClose={() => setAddStore(null)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add store</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize={type.size.small} color={neutral[600]} mb={2}>The retailer's store code (Dis-Chem e.g. S217, Checkers e.g. 0185, Clicks e.g. 384). The name is filled from the retailer's store list when the code is known.</Text>
            <HStack mb={3}>
              <Select maxW="160px" value={addStore?.retailer || "DIS"} onChange={(e) => setAddStore({ ...addStore, retailer: e.target.value })}>
                <option value="DIS">Dis-Chem</option><option value="CHK">Checkers</option><option value="CLI">Clicks</option>
              </Select>
              <Input placeholder="Store code" value={addStore?.site_code || ""} onChange={(e) => setAddStore({ ...addStore, site_code: e.target.value })} />
            </HStack>
            <Input mb={3} placeholder="Store name (needed only for a code the retailer list does not have)" value={addStore?.site_description || ""} onChange={(e) => setAddStore({ ...addStore, site_description: e.target.value })} />
            <Input mb={3} placeholder="Town (optional)" value={addStore?.town || ""} onChange={(e) => setAddStore({ ...addStore, town: e.target.value })} />
            <Select placeholder="Rep" value={addStore?.rep || ""} onChange={(e) => setAddStore({ ...addStore, rep: e.target.value })}>
              {(storeReps.length ? storeReps : me?.reps || []).map((r) => <option key={r} value={r}>{r}</option>)}
            </Select>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={() => setAddStore(null)}>Cancel</Button>
            <Button colorScheme="teal" onClick={saveAddStore} isLoading={busy} isDisabled={!addStore?.site_code || !addStore?.rep}>Add &amp; re-plan</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={!!addRep} onClose={() => setAddRep(null)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add rep</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Input mb={3} placeholder="Rep name (as it should show everywhere)" value={addRep?.rep_name || ""} onChange={(e) => setAddRep({ ...addRep, rep_name: e.target.value })} />
            <Text fontSize={type.size.small} color={neutral[600]} mb={2}>Home address (found on the map), or the coordinates. The route starts and ends here.</Text>
            <Input mb={3} placeholder="e.g. 12 Main Road, Constantia, Cape Town" value={addRep?.home_label || ""} onChange={(e) => setAddRep({ ...addRep, home_label: e.target.value })} />
            <HStack mb={3}>
              <Input placeholder="Latitude (optional)" value={addRep?.lat || ""} onChange={(e) => setAddRep({ ...addRep, lat: e.target.value })} />
              <Input placeholder="Longitude (optional)" value={addRep?.lng || ""} onChange={(e) => setAddRep({ ...addRep, lng: e.target.value })} />
            </HStack>
            <Text fontSize={type.size.small} color={neutral[600]} mb={1}>Stores per day</Text>
            <NumberInput min={1} max={15} value={addRep?.stores_per_day || 6} onChange={(v) => setAddRep({ ...addRep, stores_per_day: Number(v) })} maxW="120px"><NumberInputField /></NumberInput>
            <Text fontSize={type.size.caption} color={neutral[500]} mt={3}>Then give the rep stores on Data → Stores. The rep's phone login is made on Users → Reps.</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={() => setAddRep(null)}>Cancel</Button>
            <Button colorScheme="teal" onClick={saveAddRep} isLoading={busy} isDisabled={!addRep?.rep_name || addRep.rep_name.trim().length < 3}>Add rep</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={!!edit} onClose={() => setEdit(null)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Home — {edit?.rep_name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize={type.size.small} color={neutral[600]} mb={2}>Street address (found on the map), or the coordinates. Saving re-plans this rep from today.</Text>
            <Input mb={3} placeholder="e.g. 12 Main Road, Constantia, Cape Town" value={edit?.label || ""} onChange={(e) => setEdit({ ...edit, label: e.target.value })} />
            <HStack mb={3}>
              <Input placeholder="Latitude (optional)" value={edit?.lat || ""} onChange={(e) => setEdit({ ...edit, lat: e.target.value })} />
              <Input placeholder="Longitude (optional)" value={edit?.lng || ""} onChange={(e) => setEdit({ ...edit, lng: e.target.value })} />
            </HStack>
            <Text fontSize={type.size.small} color={neutral[600]} mb={1}>Stores per day</Text>
            <NumberInput min={1} max={15} value={edit?.stores_per_day || 6} onChange={(v) => setEdit({ ...edit, stores_per_day: Number(v) })} maxW="120px"><NumberInputField /></NumberInput>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={() => setEdit(null)}>Cancel</Button>
            <Button colorScheme="teal" onClick={saveHome} isLoading={busy} isDisabled={!edit?.label && (edit?.lat === "" || edit?.lng === "")}>Save &amp; re-plan</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}

export default RepTracker
